// The pure dead-bee corpse lifecycle (bee-visual-states bug c, design "Dead-bee corpse
// presentation", client-rendering). The wire carries no death signal at all: a
// `CloseDelta`'s deletes are computed generically as "present last accepted frame, absent
// now" (`waggle-server/src/streaming/machine.rs`), identically whether a bee died, walked
// out of the subscriber's viewport (the close-tier LOD projector already filters to bees
// inside the viewport rect), or was dropped by `close_lod_cap` truncation churn
// (`ClosePayload.truncated`). So this is a client-side HEURISTIC, not an authoritative
// determination (tracked as a `docs/dev/deferred.md` limitation): a disappearance is
// classified as churn (the last accepted frame it appeared in reported `truncated: true`)
// or a viewport exit (its last known screen position sat within a small edge margin of the
// viewport) — neither spawns a corpse — and everything else is treated as death, spawning a
// corpse frozen at the bee's last known world position and heading.
//
// Mirrors the accepted-frame/wall-clock split already established by `FacingDeriver`
// (`onCloseFrame` ages/classifies once per accepted close-tier frame; `advance` ages the
// held/fading corpses in wall-clock time, read every RAF frame like `CrossfadeManager`) and
// the epoch-driven reset `FacingDeriver` already implements (a `connectionEpoch` change —
// reconnect — clears tracked state so no stale corpse carries across an identity
// discontinuity). Pure and Pixi-free: `CanvasRenderer` (group 11 GPU wiring) feeds
// `onCloseFrame` once per accepted close frame, `advance` once per RAF frame, and reads
// `corpses` to merge grey, grounded, fading drawables into the drawn frame (D-086: the GPU
// paint itself stays on-device-exercised only).

/** One close-tier bee's per-frame projection the tracker needs to classify a later
 * disappearance: its world position (for a spawned corpse to freeze at), its projected
 * screen position (for the viewport-edge-exit check — computed by the caller via
 * `worldToScreen`, never by this Pixi-free module), and its current derived heading
 * (frozen into the corpse so it doesn't keep re-deriving after death). */
import { ticksPer } from './tick-window';

export interface CorpseFrameSample {
  readonly id: number;
  readonly position: readonly [number, number];
  readonly screenX: number;
  readonly screenY: number;
  readonly heading: number;
}

/** The screen-pixel viewport size the edge-margin check is relative to (the caller's
 * `app.screen.width/height` — the same source `drawTier` already reads for `depthScale`). */
export interface ViewportSize {
  readonly width: number;
  readonly height: number;
}

/** The one wire-derived cap-pressure signal the churn arm of the heuristic reads —
 * `ClosePayload.truncated`, already plumbed onto `WorldSnapshot` for the degradation
 * monitor (`degradationSignalFor`). No new wire field. */
export interface CorpseFrameSignal {
  readonly truncated: boolean;
}

/** The discontinuity signals the tracker reads: design D9's `connectionEpoch` (a reconnect) and
 * the replay `playhead` (WAG-9). Deliberately a structural subset of `RenderInput`, so a full
 * render-input is passed directly — which is the point, not a convenience: a call site that
 * rebuilt this as an object literal would silently drop a field added here, and the renderer's
 * `render()` body is outside the unit-tested surface. */
export interface CorpseEpochs {
  readonly connectionEpoch: number;
  /** The replay playhead tick, or `null` in live play. A non-contiguous move is a
   * discontinuity — see {@link CorpseTracker.onCloseFrame}. */
  readonly playhead: number | null;
}

/** A live, held-or-fading corpse: frozen position/heading, and the current presentation
 * alpha (1 while held, linearly fading to 0 over the fade window, then removed). */
export interface Corpse {
  readonly id: number;
  readonly position: readonly [number, number];
  readonly heading: number;
  readonly alpha: number;
}

/** How long a spawned corpse holds at full alpha before it starts fading (ms). */
export const DEFAULT_HOLD_MS = 1500;

/** How long a held corpse takes to fade from full alpha to zero once its hold expires
 * (ms) — the corpse is removed once hold + fade has elapsed. */
export const DEFAULT_FADE_MS = 500;

/** Fraction of the viewport's width/height, from each edge, treated as "near the edge" for
 * the viewport-exit arm of the heuristic — a disappearance whose last known screen position
 * falls inside this margin is presumed to have walked out of view rather than died. */
export const DEFAULT_EDGE_MARGIN_FRACTION = 0.08;

export interface CorpseTrackerOptions {
  /**
   * The running sim tick rate (Hz), used to derive {@link DEFAULT_HOLD_MS}/
   * {@link DEFAULT_FADE_MS} into ticks when `holdTicks`/`fadeTicks` are not given explicitly.
   *
   * **wire-required** (client-tick-rate-provenance): the corpse tracker runs after a
   * handshake, so this is a REQUIRED parameter sourced from `Welcome.tick_rate_hz` /
   * `manifest.tickRateHz` — no defaulted argument, no `?? 15`, and no import of the generated
   * `SIM_TICK_RATE_HZ` mirror. A caller with no rate to pass must fail to compile rather than
   * silently deriving windows against a build default that may not match the rate a replay's
   * playhead is actually ticking at.
   */
  readonly tickRateHz: number;
  holdMs?: number;
  fadeMs?: number;
  /** The playhead-anchored hold window, in ticks. Defaults to `ticksPer(holdMs, tickRateHz)`. */
  holdTicks?: number;
  /** The playhead-anchored fade window, in ticks. Defaults to `ticksPer(fadeMs, tickRateHz)`. */
  fadeTicks?: number;
  edgeMarginFraction?: number;
}

interface LastSeenBee {
  readonly position: readonly [number, number];
  readonly screenX: number;
  readonly screenY: number;
  readonly heading: number;
}

interface ActiveCorpse {
  readonly position: readonly [number, number];
  readonly heading: number;
  elapsedMs: number;
  /** The playhead tick the death was classified on, or `null` when it was classified in live play
   * (no playhead). Non-null selects the tick-anchored fade over the wall-clock one. */
  readonly spawnTick: number | null;
}

/** True when `(screenX, screenY)` falls within `marginFraction` of any viewport edge. */
function isNearViewportEdge(
  screenX: number,
  screenY: number,
  viewport: ViewportSize,
  marginFraction: number,
): boolean {
  const marginX = viewport.width * marginFraction;
  const marginY = viewport.height * marginFraction;
  return (
    screenX <= marginX ||
    screenX >= viewport.width - marginX ||
    screenY <= marginY ||
    screenY >= viewport.height - marginY
  );
}

export class CorpseTracker {
  private readonly holdMs: number;
  private readonly fadeMs: number;
  private readonly holdTicks: number;
  private readonly fadeTicks: number;
  private readonly edgeMarginFraction: number;

  private lastSeen = new Map<number, LastSeenBee>();
  private lastSignalTruncated = false;
  private lastViewport: ViewportSize | null = null;
  private readonly active = new Map<number, ActiveCorpse>();

  private lastConnectionEpoch = -1;
  private lastPlayhead: number | null = null;
  private initializedEpoch = false;

  constructor(options: CorpseTrackerOptions) {
    this.holdMs = options.holdMs ?? DEFAULT_HOLD_MS;
    this.fadeMs = options.fadeMs ?? DEFAULT_FADE_MS;
    this.holdTicks = options.holdTicks ?? ticksPer(this.holdMs, options.tickRateHz);
    this.fadeTicks = options.fadeTicks ?? ticksPer(this.fadeMs, options.tickRateHz);
    this.edgeMarginFraction = options.edgeMarginFraction ?? DEFAULT_EDGE_MARGIN_FRACTION;
  }

  /** Every live-or-fading corpse, in no particular order (the caller sorts alongside the
   * rest of the drawable list). A corpse past its presentation window is omitted rather than
   * returned at zero alpha, so this answers the same set `advance`'s expiry used to.
   *
   * **Must never throw**: `tierDrawables` reads this every RAF frame, so an exception here takes
   * the whole drawn frame down rather than just the corpses (WAG-9 tasks 1.3/2.2). Every arithmetic
   * path below is guarded for a null playhead, an absent spawn tick and a zero-width window. */
  get corpses(): Corpse[] {
    const result: Corpse[] = [];
    for (const [id, corpse] of this.active) {
      const alpha = this.alphaOf(corpse);
      if (alpha === null) {
        continue; // its presentation window has fully elapsed
      }
      result.push({ id, position: corpse.position, heading: corpse.heading, alpha });
    }
    return result;
  }

  /**
   * One corpse's presentation alpha, or `null` once its window has fully elapsed.
   *
   * A corpse classified under a playhead ages in **ticks past the tick it died on** — so a held
   * playhead holds it, and a scrub does not smear its fade across whatever wall-clock time the
   * player spent dragging. One classified in live play (no playhead) keeps the wall-clock ageing
   * `advance` drives, unchanged.
   */
  private alphaOf(corpse: ActiveCorpse): number | null {
    const playhead = this.lastPlayhead;
    if (corpse.spawnTick !== null && playhead !== null) {
      const elapsed = playhead - corpse.spawnTick;
      if (elapsed >= this.holdTicks + this.fadeTicks) {
        return null;
      }
      if (elapsed <= this.holdTicks) {
        return 1;
      }
      return this.fadeTicks <= 0
        ? 0
        : Math.max(0, 1 - (elapsed - this.holdTicks) / this.fadeTicks);
    }
    const fadeElapsed = corpse.elapsedMs - this.holdMs;
    if (fadeElapsed <= 0) {
      return 1;
    }
    return this.fadeMs <= 0 ? 0 : Math.max(0, 1 - fadeElapsed / this.fadeMs);
  }

  /**
   * True when the playhead moved **non-contiguously** since the last accepted frame — backwards by
   * any amount, or forwards by more than the presentation window. Mirrors
   * `PresentClock.shouldReset`'s treatment of a backward playhead, widened for the forward case.
   *
   * The forward threshold is the window rather than a single tick because the Sandbox offers 4× and
   * 16× playback, which advance the playhead several ticks per rendered frame; a one-tick threshold
   * would reset during ordinary fast playback and suppress corpses entirely. It is an **inference,
   * not a seek signal** (D-219): a long enough gap between rendered frames at 16× reads as a seek
   * and withholds a corpse a smooth frame rate would have shown. That is the safe direction — it
   * withholds rather than invents, and inventing is the defect being corrected.
   *
   * A mode flip (playhead appearing or disappearing) counts too; it cannot happen for a live
   * renderer instance, but reading state must not be what discovers it if it ever does.
   */
  private playheadBroke(playhead: number | null): boolean {
    const last = this.lastPlayhead;
    if (playhead === null || last === null) {
      return playhead !== last;
    }
    return playhead < last || playhead - last > this.holdTicks + this.fadeTicks;
  }

  /**
   * Consume one accepted close-tier sample frame: classify every id present last frame but
   * absent now (churn / viewport-exit / death — spawning a corpse only for death), and clear
   * a corpse whose id reappears live this frame (a resurrection safety net against a
   * false-positive death classification). A `connectionEpoch` change (reconnect) clears all
   * tracked state first, so no stale corpse or bookkeeping carries across an identity
   * discontinuity — mirrors `FacingDeriver`'s reconnect reset.
   */
  onCloseFrame(
    samples: Iterable<CorpseFrameSample>,
    signal: CorpseFrameSignal,
    viewport: ViewportSize,
    epochs: CorpseEpochs,
  ): void {
    if (!this.initializedEpoch) {
      // The first frame only adopts a baseline, never resets — otherwise the first accepted frame
      // of every replay would be inert (mirrors `PresentClock.shouldReset`'s first-frame rule).
      this.initializedEpoch = true;
      this.lastConnectionEpoch = epochs.connectionEpoch;
      this.lastPlayhead = epochs.playhead;
    } else if (
      epochs.connectionEpoch !== this.lastConnectionEpoch ||
      this.playheadBroke(epochs.playhead)
    ) {
      this.resetState();
      this.lastConnectionEpoch = epochs.connectionEpoch;
      this.lastPlayhead = epochs.playhead;
    } else {
      this.lastPlayhead = epochs.playhead;
    }

    // Drop corpses whose window elapsed at this playhead, so an expired record cannot accumulate
    // between resets. `advance` does the same for the wall-clock ones.
    for (const [id, corpse] of this.active) {
      if (corpse.spawnTick !== null && this.alphaOf(corpse) === null) {
        this.active.delete(id);
      }
    }

    const present = new Set<number>();
    const nextLastSeen = new Map<number, LastSeenBee>();
    for (const sample of samples) {
      present.add(sample.id);
      nextLastSeen.set(sample.id, {
        position: sample.position,
        screenX: sample.screenX,
        screenY: sample.screenY,
        heading: sample.heading,
      });
      // A live reappearance clears any tracked corpse for this id (a false-positive death).
      this.active.delete(sample.id);
    }

    for (const [id, last] of this.lastSeen) {
      if (present.has(id)) continue; // still alive this frame
      const churn = this.lastSignalTruncated;
      const viewportExit =
        this.lastViewport !== null &&
        isNearViewportEdge(last.screenX, last.screenY, this.lastViewport, this.edgeMarginFraction);
      if (!churn && !viewportExit) {
        this.active.set(id, {
          position: last.position,
          heading: last.heading,
          elapsedMs: 0,
          // The tick this death was classified on — what the fade is anchored to.
          spawnTick: epochs.playhead,
        });
      }
    }

    this.lastSeen = nextLastSeen;
    this.lastSignalTruncated = signal.truncated;
    this.lastViewport = viewport;
  }

  /** Ages every held/fading corpse by `elapsedMs` (wall-clock RAF delta), expiring one once
   * its hold+fade window has fully elapsed. Read every RAF frame, independent of whether a
   * new accepted close frame has arrived (like `CrossfadeManager.advance`). */
  advance(elapsedMs: number): void {
    for (const [id, corpse] of this.active) {
      if (corpse.spawnTick !== null) {
        // Playhead-anchored: the playhead ages it, not the clock (WAG-9). Skipping here is what
        // makes a paused replay hold its corpses instead of fading them out under a still playhead.
        continue;
      }
      corpse.elapsedMs += elapsedMs;
      if (corpse.elapsedMs >= this.holdMs + this.fadeMs) {
        this.active.delete(id);
      }
    }
  }

  /** Clears every tracked corpse and last-seen bookkeeping — called by the caller on a
   * tier change (close → wide; corpses are a close-tier-only concept) in addition to the
   * internal reconnect reset `onCloseFrame` already performs. */
  clear(): void {
    this.resetState();
  }

  private resetState(): void {
    this.active.clear();
    this.lastSeen.clear();
    this.lastSignalTruncated = false;
    this.lastViewport = null;
  }
}
